import {
  type Component,
  defineComponent,
  ExtractPublicPropTypes,
  h,
} from 'vue';
import {
  createRouter,
  createWebHashHistory,
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteRecordRaw,
} from 'vue-router';
import api from '@/api';
import { PRODUCT_NAME } from '@/constants';
import {
  LoginRequiredError,
  resolveAccessDenialReason,
  resolveFallbackLocation,
  resolveLastUsedAgentLocation,
} from '@/routers-capability/accessGuard';
import {
  MYPAGE_ROUTE_META,
  MYPAGE_ROUTE_META_TYPE,
  MYPAGE_ROUTE_NAMES,
} from '@/routers-capability/constants';
import {
  type ShellRouteGroup,
  withMenuSection,
  withoutMenuSection,
} from '@/routers-capability/routeMeta';
import {
  redirectToCurrentDesign,
  redirectToMypage,
  shouldRedirectToLogin,
  shouldRedirectToMypage,
} from '@/routers-capability/routingCommon';
import store from '@/stores/pcStore';
import * as Sentry from '@sentry/vue';
import ContractMemberAdmin from '@/components-capability/admin/contracts/contract-member-admin.vue';
import EnvironmentSettings from '@/components-capability/admin/environment-settings/environment-settings.vue';
import GroupAdmin from '@/components-capability/admin/groups/group-admin.vue';
import IpAddressRestrictions from '@/components-capability/admin/ip-address-restriction/ip-address-restrictions.vue';
import Members from '@/components-capability/admin/members/members.vue';
import SamlFederationDetail from '@/components-capability/admin/saml/saml-federation-detail.vue';
import SamlFederationList from '@/components-capability/admin/saml/saml-federation-list.vue';
import ThemeAdmin from '@/components-capability/admin/themes/theme-admin.vue';
import ThemeMemberAdmin from '@/components-capability/admin/themes/theme-member-admin.vue';
import BoxFolderCreate from '@/components-capability/admin/user-documents/box-folder-create.vue';
import BoxFolderEdit from '@/components-capability/admin/user-documents/box-folder-edit.vue';
import BoxFolders from '@/components-capability/admin/user-documents/box-folders.vue';
import SharepointCredentialForm from '@/components-capability/admin/user-documents/sharepoint-credential-form.vue';
import SharepointSitesEdit from '@/components-capability/admin/user-documents/sharepoint-site-edit.vue';
import SharepointSites from '@/components-capability/admin/user-documents/sharepoint-sites.vue';
import OrganizationTagDetail from '@/components-capability/others/organization-tags/organization-tag-detail/organization-tag-detail.vue';
import SettingsMediaDisplay from '@/components-capability/settings/media-display.vue';
import UserSettingsEmail from '@/components-capability/settings/user-settings-email.vue';
import UserSettingsNews from '@/components-capability/settings/user-settings-news.vue';
import UserSettingsNotification from '@/components-capability/settings/user-settings-notification.vue';
import UserSettingsPassword from '@/components-capability/settings/user-settings-password.vue';
import UserSettingsProfile from '@/components-capability/settings/user-settings-profile.vue';
import ContractAdmin from '@/components/admin/contracts/contract-admin.vue';
import ContractCreditAdmin from '@/components/admin/contracts/contract-credit-admin.vue';
import ContractCreditGrantAdmin from '@/components/admin/contracts/contract-credit-grant-admin.vue';
import ContractCreditUsageAdmin from '@/components/admin/contracts/contract-credit-usage-admin.vue';
import LegacyContractMemberAdmin from '@/components/admin/contracts/contract-member-admin.vue';
import LegacyEnvironmentSettings from '@/components/admin/environment-settings/environment-settings.vue';
import LegacyGroupAdmin from '@/components/admin/groups/group-admin.vue';
import LegacyIpAddressRestrictions from '@/components/admin/ip-address-restriction/ip-address-restrictions.vue';
import LegacyMembers from '@/components/admin/members/members.vue';
import LegacySamlFederationDetail from '@/components/admin/saml/saml-federation-detail.vue';
import LegacySamlFederationList from '@/components/admin/saml/saml-federation-list.vue';
import LegacyThemeAdmin from '@/components/admin/themes/theme-admin.vue';
import LegacyThemeMemberAdmin from '@/components/admin/themes/theme-member-admin.vue';
import AnewsStorageIndex from '@/components/admin/user-documents/anews-storage-index.vue';
import LegacyBoxFolderCreate from '@/components/admin/user-documents/box-folder-create.vue';
import LegacyBoxFolderEdit from '@/components/admin/user-documents/box-folder-edit.vue';
import LegacyBoxFolders from '@/components/admin/user-documents/box-folders.vue';
import BoxIndex from '@/components/admin/user-documents/box-index.vue';
import DatasourceUsages from '@/components/admin/user-documents/datasource-usages.vue';
import LegacySharepointCredentialForm from '@/components/admin/user-documents/sharepoint-credential-form.vue';
import SharepointIndex from '@/components/admin/user-documents/sharepoint-index.vue';
import LegacySharepointSitesEdit from '@/components/admin/user-documents/sharepoint-site-edit.vue';
import LegacySharepointSites from '@/components/admin/user-documents/sharepoint-sites.vue';
import AdpDocumentPage from '@/components/adp-document/adp-document.vue';
import Comment from '@/components/comment/comment.vue';
import GroupActivities from '@/components/group/group-activities/group-activities.vue';
import GroupDetail from '@/components/group/group-detail.vue';
import GroupEdit from '@/components/group/group-edit.vue';
import GroupMarks from '@/components/group/group-marks.vue';
import GroupMembers from '@/components/group/group-members.vue';
import GroupSearch from '@/components/group/group-search.vue';
import GroupTagList from '@/components/group/group-tag-list.vue';
import AnewsHomeSwitcher from '@/components/home/anews-home-switcher.vue';
import followLists from '@/components/home/follow-lists/follow-lists.vue';
import InsightsPage from '@/components/insights/insights-page.vue';
import InsightsSaved from '@/components/insights/insights-saved.vue';
import InsightsToday from '@/components/insights/insights-today.vue';
import MainViewSwitcher from '@/components/layouts/main-view-switcher.vue';
import Notifications from '@/components/notifications/notifications.vue';
import LegacyOrganizationTagDetail from '@/components/others/organization-tags/organization-tag-detail/organization-tag-detail.vue';
import OtherOrganizationTags from '@/components/others/organization-tags/organization-tag-list/organization-tag-list.vue';
import PersonalNewsEdit from '@/components/personal-news/personal-news-edit.vue';
import PersonalNews from '@/components/personal-news/personal-news.vue';
import PersonalPatents from '@/components/personal-news/personal-patents.vue';
import PersonalResearchPapersNew from '@/components/personal-news/personal-research-papers.vue';
import redirectEmailPage from '@/components/redirect-page/email.vue';
import redirectWebPushPage from '@/components/redirect-page/web-push.vue';
import PromoSearchResults from '@/components/search/promotion-user-document-trial/promo-search-results.vue';
import SearchResults from '@/components/search/search-results.vue';
import LegacySettingsMediaDisplay from '@/components/settings/media-display.vue';
import LegacyUserSettingsEmail from '@/components/settings/user-settings-email.vue';
import UserSettingsMark from '@/components/settings/user-settings-mark.vue';
import LegacyUserSettingsNews from '@/components/settings/user-settings-news.vue';
import LegacyUserSettingsNotification from '@/components/settings/user-settings-notification.vue';
import LegacyUserSettingsPassword from '@/components/settings/user-settings-password.vue';
import LegacyUserSettingsProfile from '@/components/settings/user-settings-profile.vue';
import ShareSurveyPage from '@/components/share-survey/containers/share-survey-page.vue';
import ShareSurveys from '@/components/share-survey/containers/share-surveys.vue';
import SurveySession from '@/components/survey/containers/survey-session.vue';
import SurveyTop from '@/components/survey/containers/survey-top.vue';
import Surveys from '@/components/survey/containers/surveys.vue';
import IndustryNews from '@/components/team/industry-news.vue';
import TeamFeaturedArticles from '@/components/team/team-featured-articles.vue';
import ThemeEdit from '@/components/theme/edit/theme-edit.vue';
import ThemePatents from '@/components/theme/patents.vue';
import ThemeResearchPapers from '@/components/theme/research-papers.vue';
import ThemeKeywordFeed from '@/components/theme/theme-keyword-feed.vue';
import ThemeLayout from '@/components/theme/theme-layout.vue';
import ThemeRelatedFeed from '@/components/theme/theme-related-feed.vue';
import ThemeSearch from '@/components/theme/theme-search.vue';
import UserDocumentPage from '@/components/user-document/user-document-preview-page.vue';
import MyTags from '@/components/user/my-tags.vue';
import MypageComments from '@/components/user/mypage-comments.vue';
import MypageMarks from '@/components/user/mypage-marks.vue';
import MypageViews from '@/components/user/mypage-views.vue';
import UserMarks from '@/components/user/user-marks.vue';
import { isMobileUser } from '@/utils';
import { getLayoutScrollElement } from '@/utils/composables/useLayoutScrollContainer';
import { useIsNextLayout } from '@/utils/composables/useLayoutVariant';
import { isDynamicImportError } from '@/utils/dynamicImportError';
import { recordAgentVisit } from '@/utils/lastUsedAgent';
import { clearLocalSession } from '@/utils/signout';
import { TECH_EXPLORATION_ANCHOR_QUERY_KEY } from '@/utils/techExplorations/anchors';
import { userSession } from '@/utils/userSession';
import { useGTag } from '@/utils/vue';
import { accountRouteConfig } from './accountRoutes';
import { dashboardRouteConfig } from './dashboardRoutes';
import { fmeaRouteConfig } from './fmeaRoutes';
import { forcedIdeationRouteConfig } from './forcedIdeationRoutes';
import { labsRouteConfig } from './labsRoutes';
import { marketSizingRouteConfig } from './marketSizingRoutes';
import { mobileRoutes } from './mobileRoutes';
import { onboardingRouteConfig } from './onboardingRoutes';
import { patentFtoRouteConfig } from './patentFtoRoutes';
import { techExplorationRouteConfigs } from './techExplorationRoutes';
import { featureFlags } from '@/featureFlags';

type NotificationProps = Partial<
  ExtractPublicPropTypes<InstanceType<typeof Notifications>>
>;

/**
 * 旧デザインの中では、販売プラン対応前の画面をそのまま出す。
 * 情報収集を使えない人は旧デザインを選んでいても新しいシェルで開くので、
 * デザインの設定ではなく、実際に描画しているシェルで決める。
 */
const forDesign = (next: Component, legacy: Component): Component =>
  defineComponent({
    inheritAttrs: false,
    setup(_, { attrs }) {
      const isNextLayout = useIsNextLayout();
      return () => h(isNextLayout ? next : legacy, attrs);
    },
  });

/**
 * 共通シェル（ヘッダー + 左メニュー）配下のページ。
 *
 * ページを追加するときは、該当セクションの withMenuSection(...) の中に入れる。
 * 左メニューを出さないページは withoutMenuSection(...) に入れる。
 * どちらも通さないルートは ShellRouteGroup を満たさず、コンパイルエラーになる。
 *
 * セクションの新設手順は README「ルーティングと左メニュー」を参照。
 */
export const shellRoutes: ShellRouteGroup[] = [
  withMenuSection(
    'news',
    [
      {
        path: '/',
        component: AnewsHomeSwitcher,
        name: 'anewsHome',
        meta: { title: 'ホーム', accessScope: 'authenticated' },
      },
      {
        path: '/themes/:themeId/edit',
        component: ThemeEdit,
        name: 'themeEdit',
        meta: { title: 'テーマ', type: 'theme' },
        props: true,
      },
      {
        path: '/themes/create',
        component: ThemeEdit,
        name: 'themeCreate',
        meta: { title: 'テーマ作成' },
      },
      {
        path: '/themes/search',
        component: ThemeSearch,
        name: 'themeSearch',
        meta: { title: 'テーマ検索' },
      },
      {
        path: '/themes/:themeId',
        component: ThemeLayout,
        props: true,
        children: [
          {
            path: 'foreign_feed',
            redirect: { name: 'themeFeed' },
            meta: { title: 'テーマ', type: 'theme' },
          },
          {
            path: 'related_news',
            component: ThemeRelatedFeed,
            name: 'themeRelatedNews',
            meta: { title: 'テーマ', type: 'theme' },
          },
          {
            path: '',
            component: ThemeKeywordFeed,
            name: 'themeFeed',
            meta: { title: 'テーマ', type: 'theme' },
          },
          {
            path: 'technical_literature',
            name: 'themeTechnicalLiterature',
            redirect: { name: 'themeResearchPapers' },
            meta: { title: 'テーマ', type: 'theme' },
          },
          {
            path: 'research_papers',
            component: ThemeResearchPapers,
            name: 'themeResearchPapers',
            meta: { title: 'テーマ', type: 'theme' },
          },
          {
            path: 'patents',
            component: ThemePatents,
            name: 'themePatents',
            meta: { title: 'テーマ', type: 'theme' },
          },
        ],
      },
      {
        path: '/personal-news',
        component: PersonalNews,
        name: 'personalNews',
        meta: { title: 'パーソナルニュース' },
      },
      {
        path: '/personal-news/edit',
        component: PersonalNewsEdit,
        name: 'personalNewsEdit',
        meta: { title: 'パーソナルニュース設定' },
      },
      {
        path: '/personal-research-papers',
        component: PersonalResearchPapersNew,
        name: 'personalResearchPapers',
        meta: { title: 'パーソナル論文' },
      },
      {
        path: '/personal-patents',
        component: PersonalPatents,
        name: 'personalPatents',
        meta: { title: 'パーソナル特許' },
      },
      {
        path: '/articles/:id',
        component: AdpDocumentPage,
        name: 'articlePage',
        meta: { title: '記事詳細' },
        props: route => ({
          id: route.params.id,
          doc_type: 'article',
        }),
      },
      {
        path: '/research_papers/:id',
        component: AdpDocumentPage,
        name: 'researchPaperPage',
        meta: { title: '論文詳細' },
        props: route => ({
          id: route.params.id,
          doc_type: 'research_paper',
        }),
      },
      {
        path: '/patents/:id',
        component: AdpDocumentPage,
        name: 'patentPage',
        meta: { title: '特許詳細' },
        props: route => ({
          id: route.params.id,
          doc_type: 'patent',
        }),
      },
      {
        path: '/reports/:id',
        component: AdpDocumentPage,
        name: 'reportPage',
        meta: { title: 'レポート詳細' },
        props: route => ({
          id: route.params.id,
          doc_type: 'report',
        }),
      },
      {
        path: '/user_documents/:id',
        component: UserDocumentPage,
        name: 'userDocumentPreviewPage',
        meta: { title: '社内情報プレビュー' },
        props: route => ({
          id: route.params.id,
          doc_type: 'user_document',
          page: route.query.page ? Number(route.query.page) : 1,
        }),
      },
      {
        path: '/team/featured_articles',
        component: TeamFeaturedArticles,
        name: 'teamFeaturedArticles',
        meta: { title: '注目記事' },
      },
      {
        path: '/team/industry_news',
        component: IndustryNews,
        name: 'industryNews',
        meta: { title: '業界ニュース' },
      },
      {
        path: '/follow_lists',
        component: followLists,
        name: 'followLists',
        meta: { title: 'フォローリスト' },
      },
    ],
    'information_retrieval',
  ),
  withMenuSection('admin', [
    {
      path: '/admin/members',
      component: forDesign(Members, LegacyMembers),
      name: 'members',
      meta: { title: 'メンバー管理', accessScope: 'authenticated' },
    },
    {
      path: '/admin/environment_settings',
      component: forDesign(EnvironmentSettings, LegacyEnvironmentSettings),
      name: 'environmentSettings',
      meta: { title: '環境設定', accessScope: 'information_retrieval' },
    },
    {
      path: '/admin/ip_address_restrictions',
      component: forDesign(IpAddressRestrictions, LegacyIpAddressRestrictions),
      name: 'ipAddressRestrictions',
      meta: { title: 'IPアドレス制限', accessScope: 'authenticated' },
    },
    {
      path: '/admin/contracts',
      component: ContractAdmin,
      name: 'contracts',
      meta: {
        title: '契約・クレジット管理',
        accessScope: 'authenticated',
      },
    },
    {
      path: '/admin/contracts/:contractId/members',
      component: forDesign(ContractMemberAdmin, LegacyContractMemberAdmin),
      name: 'contractMembers',
      meta: {
        title: '契約管理者',
        accessScope: 'authenticated',
      },
    },
    {
      path: '/admin/contracts/:contractId/credits',
      component: ContractCreditAdmin,
      name: 'contractCredits',
      meta: {
        title: 'クレジット管理',
        accessScope: 'authenticated',
      },
      redirect: { name: 'contractCreditsUsages' },
      children: [
        {
          path: 'usages',
          component: ContractCreditUsageAdmin,
          name: 'contractCreditsUsages',
          meta: {
            title: 'クレジット管理',
          },
        },
        {
          path: 'grants',
          component: ContractCreditGrantAdmin,
          name: 'contractCreditsGrants',
          meta: {
            title: 'クレジット管理',
          },
        },
      ],
    },
    {
      path: '/admin/themes',
      component: forDesign(ThemeAdmin, LegacyThemeAdmin),
      name: 'themeAdmin',
      meta: { title: 'テーマ管理', accessScope: 'information_retrieval' },
    },
    {
      path: '/admin/groups',
      component: forDesign(GroupAdmin, LegacyGroupAdmin),
      name: 'groupAdmin',
      meta: { title: 'グループ管理', accessScope: 'information_retrieval' },
    },
    {
      path: '/admin/user_documents/aconnect_storage',
      component: AnewsStorageIndex,
      name: 'anewsStorageIndex',
      meta: {
        title: '社内情報ストレージ管理',
        accessScope: 'authenticated',
      },
    },
    {
      path: '/admin/user_documents/box',
      component: BoxIndex,
      name: 'boxIndex',
      meta: {
        title: '社内情報ストレージ管理',
        accessScope: 'authenticated',
      },
    },
    {
      path: '/admin/user_documents/sharepoint',
      component: SharepointIndex,
      name: 'sharepointIndex',
      meta: {
        title: '社内情報ストレージ管理',
        accessScope: 'authenticated',
      },
    },
    {
      path: '/admin/user_documents/datasource_usages',
      component: DatasourceUsages,
      name: 'datasourceUsages',
      meta: {
        title: '社内情報ストレージ管理',
        accessScope: 'authenticated',
      },
    },
    {
      path: '/admin/user_documents/box_folders/create',
      component: forDesign(BoxFolderCreate, LegacyBoxFolderCreate),
      name: 'boxFolderCreate',
      meta: { title: 'Boxフォルダ登録', accessScope: 'authenticated' },
    },
    {
      path: '/admin/user_documents/box_folders',
      component: forDesign(BoxFolders, LegacyBoxFolders),
      name: 'boxFolders',
      meta: { title: 'Boxフォルダ一覧', accessScope: 'authenticated' },
    },
    {
      path: '/admin/user_documents/box_folders/edit',
      component: forDesign(BoxFolderEdit, LegacyBoxFolderEdit),
      name: 'boxFolderEdit',
      meta: { title: 'Boxフォルダ情報', accessScope: 'authenticated' },
    },
    {
      path: '/admin/user_documents/sharepoint_credential/create',
      name: 'sharepointCredentialCreate',
      meta: {
        title: 'SharePoint認証情報登録',
        accessScope: 'authenticated',
      },
      component: forDesign(
        SharepointCredentialForm,
        LegacySharepointCredentialForm,
      ),
    },
    {
      path: '/admin/user_documents/sharepoint_sites/edit',
      name: 'sharepointSitesEdit',
      meta: {
        title: 'SharePointサイトの編集',
        accessScope: 'authenticated',
      },
      component: forDesign(SharepointSitesEdit, LegacySharepointSitesEdit),
    },
    {
      path: '/admin/user_documents/sharepoint_sites',
      name: 'sharepointSites',
      meta: {
        title: 'SharePointサイトの情報',
        accessScope: 'authenticated',
      },
      component: forDesign(SharepointSites, LegacySharepointSites),
    },
    {
      path: '/admin/themes/:themeId/members',
      component: forDesign(ThemeMemberAdmin, LegacyThemeMemberAdmin),
      name: 'themeAdminMembers',
      meta: { title: 'テーマ管理', accessScope: 'information_retrieval' },
    },
    {
      path: '/admin/sso',
      component: forDesign(SamlFederationList, LegacySamlFederationList),
      name: 'samlFederationList',
      meta: { title: 'SSO認証', accessScope: 'authenticated' },
    },
    {
      path: '/admin/sso/edit',
      component: forDesign(SamlFederationDetail, LegacySamlFederationDetail),
      name: 'samlFederationDetail',
      meta: { title: 'SSO認証', accessScope: 'authenticated' },
    },
    {
      path: '/admin/sso/callback',
      component: forDesign(SamlFederationDetail, LegacySamlFederationDetail),
      name: 'samlFederationCallback',
      meta: { title: 'SSO認証', accessScope: 'authenticated' },
    },
    {
      path: '/admin/organization_tags',
      meta: { title: '組織タグ', accessScope: 'authenticated' },
      children: [
        {
          path: '',
          component: OtherOrganizationTags,
          name: 'adminOrganizationTags',
        },
        {
          path: ':organizationTagId',
          component: forDesign(
            OrganizationTagDetail,
            LegacyOrganizationTagDetail,
          ),
          name: 'adminOrganizationTagDetail',
          props: true,
        },
      ],
    },
    {
      path: '/admin/media_display',
      component: forDesign(SettingsMediaDisplay, LegacySettingsMediaDisplay),
      name: 'adminMediaDisplay',
      meta: {
        title: 'メディアの表示',
        availableIn: 'next',
        accessScope: 'information_retrieval',
      },
    },
  ]),
  withMenuSection(
    'group',
    [
      {
        path: '/groups',
        component: GroupSearch,
        name: 'groupList',
        meta: { title: 'グループ検索', type: 'group' },
      },
      {
        path: '/groups/:groupId',
        component: GroupActivities,
        name: 'groupActivity',
        meta: { title: 'グループ', type: 'group' },
      },
      {
        path: '/groups/:groupId/tag-list',
        component: GroupTagList,
        name: 'groupTagList',
        meta: { title: 'グループ', type: 'group' },
      },
      {
        path: '/groups/:groupId/comments',
        component: GroupDetail,
        name: 'groupDetail',
        meta: { title: 'グループ', type: 'group' },
      },
      {
        path: '/groups/:groupId/marks',
        component: GroupMarks,
        name: 'groupMarks',
        meta: { title: 'グループ', type: 'group' },
      },
      {
        path: '/groups/:groupId/edit',
        component: GroupEdit,
        name: 'groupEdit',
        meta: { title: 'グループ', type: 'group' },
      },
      {
        path: '/groups/:groupId/members',
        component: GroupMembers,
        name: 'groupMembers',
        meta: { title: 'グループ', type: 'group' },
        props: true,
      },
      {
        path: '/comments/:commentId',
        component: Comment,
        name: 'groupComment',
        meta: { title: 'グループ', type: 'group' },
      },
    ],
    'information_retrieval',
  ),
  withMenuSection('my_page', [
    {
      path: '/mypage',
      name: MYPAGE_ROUTE_NAMES.profile,
      meta: { ...MYPAGE_ROUTE_META, accessScope: 'information_retrieval' },
      redirect: { name: 'mypageMarks' },
      children: [
        {
          path: 'marks',
          component: MypageMarks,
          name: MYPAGE_ROUTE_NAMES.marks,
        },
        {
          path: 'views',
          component: MypageViews,
          name: MYPAGE_ROUTE_NAMES.views,
        },
        {
          path: 'comments',
          component: MypageComments,
          name: MYPAGE_ROUTE_NAMES.comments,
        },
        {
          path: 'export',
          component: () => import('@/components/user/user-export.vue'),
          name: MYPAGE_ROUTE_NAMES.export,
          meta: { title: 'エクスポート', type: MYPAGE_ROUTE_META_TYPE },
        },
        {
          path: 'my-tags',
          component: MyTags,
          name: MYPAGE_ROUTE_NAMES.myTags,
        },
      ],
    },
    {
      path: '/settings/profile',
      component: forDesign(UserSettingsProfile, LegacyUserSettingsProfile),
      name: 'userSettingsProfile',
      meta: {
        title: 'プロフィール',
        type: 'userSettings',
        accessScope: 'authenticated',
      },
    },
    {
      path: '/settings/news',
      component: forDesign(UserSettingsNews, LegacyUserSettingsNews),
      name: 'userSettingsNews',
      meta: {
        title: 'ニュース配信',
        type: 'userSettings',
        accessScope: 'information_retrieval',
      },
    },
    {
      path: '/settings/email',
      component: forDesign(UserSettingsEmail, LegacyUserSettingsEmail),
      name: 'userSettingsEmail',
      meta: {
        title: 'メール配信',
        type: 'userSettings',
        accessScope: 'information_retrieval',
      },
    },
    {
      path: '/settings/notification',
      component: forDesign(
        UserSettingsNotification,
        LegacyUserSettingsNotification,
      ),
      name: 'userSettingsNotification',
      meta: {
        title: '通知',
        type: 'userSettings',
        accessScope: 'information_retrieval',
      },
    },
    {
      path: '/settings/mark',
      component: UserSettingsMark,
      name: 'userSettingsMark',
      meta: {
        title: 'マーク',
        type: 'userSettings',
        accessScope: 'information_retrieval',
      },
    },
    {
      path: '/settings/password',
      component: forDesign(UserSettingsPassword, LegacyUserSettingsPassword),
      name: 'userSettingsPassword',
      meta: {
        title: 'パスワード',
        type: 'userSettings',
        accessScope: 'authenticated',
      },
    },
    {
      path: '/settings',
      name: 'userSettings',
      redirect: { name: 'userSettingsProfile' },
      meta: {
        title: 'プロフィール',
        type: 'userSettings',
        accessScope: 'authenticated',
      },
    },
  ]),
  withMenuSection(
    'others',
    [
      {
        path: '/others',
        name: 'others',
        meta: { title: 'その他' },
        redirect: () => {
          return `/others/organization_tags`;
        },
      },
      {
        path: '/others/organization_tags',
        meta: { title: '組織タグ' },
        children: [
          {
            path: '',
            component: OtherOrganizationTags,
            name: 'organizationTags',
          },
          {
            path: ':organizationTagId',
            component: forDesign(
              OrganizationTagDetail,
              LegacyOrganizationTagDetail,
            ),
            name: 'organizationTagDetail',
            props: true,
          },
        ],
      },
    ],
    'authenticated',
  ),
  withMenuSection(
    'search',
    [
      {
        path: '/search',
        component: SearchResults,
        name: 'search',
        meta: { title: '検索結果', type: 'search' },
      },
      {
        path: '/search/all',
        component: SearchResults,
        name: 'searchAll',
        meta: { title: '検索結果', type: 'search' },
      },
      {
        path: '/search/news',
        component: SearchResults,
        name: 'searchNews',
        meta: { title: '検索結果', type: 'search' },
      },
      {
        path: '/search/research_papers',
        component: SearchResults,
        name: 'searchResearchPapers',
        meta: { title: '検索結果', type: 'search' },
      },
      {
        path: '/search/patent',
        component: SearchResults,
        name: 'searchPatent',
        meta: { title: '検索結果', type: 'search' },
      },
      {
        path: '/search/report',
        component: SearchResults,
        name: 'searchReport',
        meta: { title: '検索結果', type: 'search' },
      },
      {
        path: '/search/user_document',
        component: SearchResults,
        name: 'searchUserDocument',
        meta: { title: '検索結果', type: 'search' },
      },
      {
        path: '/search/user_document_demo',
        component: PromoSearchResults,
        name: 'searchUserDocumentDemo',
        meta: { title: '検索結果', type: 'search' },
      },
    ],
    'information_retrieval',
  ),
  withMenuSection(
    'survey',
    [
      {
        path: '/surveys',
        name: 'surveys',
        meta: { title: '要約' },
        children: [
          {
            path: 'top',
            component: SurveyTop,
            name: 'surveyTop',
            meta: { title: '要約' },
          },
          {
            path: 'search',
            component: Surveys,
            name: 'searchSurvey',
            meta: { title: 'スレッド' },
          },
          {
            path: ':survey_session_id',
            component: SurveySession,
            name: 'surveySession',
            meta: { title: '要約', preserveScroll: true },
          },
        ],
      },
      {
        path: '/share_surveys',
        name: 'shareSurveys',
        meta: { title: 'シェアスレッド' },
        children: [
          {
            path: ':uuid',
            name: 'shareSurvey',
            meta: { title: 'シェアスレッド' },
            props: true,
            component: ShareSurveyPage,
          },
          {
            path: 'search',
            name: 'searchShareSurvey',
            redirect: { name: 'sharedByOthers' },
            children: [
              {
                path: 'shared_by_others',
                name: 'sharedByOthers',
                meta: { title: 'シェアスレッド' },
                component: ShareSurveys,
              },
              {
                path: 'shared_by_me',
                name: 'sharedByMe',
                meta: { title: 'シェアスレッド' },
                component: ShareSurveys,
              },
              {
                path: 'not_shared',
                name: 'notShared',
                meta: { title: 'シェアスレッド' },
                component: ShareSurveys,
              },
            ],
          },
        ],
      },
    ],
    'information_retrieval',
  ),
  withMenuSection(
    'notifications',
    [
      {
        path: '/notifications',
        redirect: '/notifications/mentions',
        name: 'notifications',
        meta: { title: 'お知らせ', type: 'notifications' },
        children: [
          {
            path: 'mentions',
            component: Notifications,
            name: 'mentionsNotifications',
            meta: {
              title: 'お知らせ',
              type: 'notifications',
            },
            props: {
              notificationGrouping: 'mentions',
            } satisfies NotificationProps,
          },
          {
            path: 'members',
            component: Notifications,
            name: 'membersNotifications',
            meta: {
              title: 'お知らせ',
              type: 'notifications',
            },
            props: {
              notificationGrouping: 'members',
            } satisfies NotificationProps,
          },
          {
            path: 'others',
            component: Notifications,
            name: 'othersNotifications',
            meta: {
              title: 'お知らせ',
              type: 'notifications',
            },
            props: {
              notificationGrouping: 'others',
            } satisfies NotificationProps,
          },
        ],
      },
    ],
    'information_retrieval',
  ),
  withMenuSection(
    'settings',
    [
      {
        path: '/settings/organization_tags',
        meta: { title: '組織タグ' },
        children: [
          {
            path: '',
            component: OtherOrganizationTags,
            name: 'settingsOrganizationTags',
          },
          {
            path: ':organizationTagId',
            component: forDesign(
              OrganizationTagDetail,
              LegacyOrganizationTagDetail,
            ),
            name: 'settingsOrganizationTagDetail',
            props: true,
          },
        ],
      },
      {
        path: '/settings/media_display',
        component: forDesign(SettingsMediaDisplay, LegacySettingsMediaDisplay),
        name: 'settingsMediaDisplay',
        // adminMediaDisplayと同一画面（役割で遷移先ルートが分かれるだけ）なのでスコープも揃える
        meta: {
          title: 'メディアの表示',
          availableIn: 'next',
          accessScope: 'information_retrieval',
        },
      },
    ],
    'authenticated',
  ),
  withMenuSection(
    'news',
    [
      {
        path: '/insights',
        component: InsightsPage,
        name: 'insights',
        redirect: { name: 'insightsToday' },
        meta: { title: '発想のヒント' },
        children: [
          {
            path: 'today',
            component: InsightsToday,
            name: 'insightsToday',
            meta: { title: '発想のヒント' },
          },
          {
            path: 'saved',
            component: InsightsSaved,
            name: 'savedInsights',
            meta: { title: '保存済み発想のヒント' },
          },
          {
            path: 'saved/:hintUuid',
            redirect: '/insights/saved',
          },
        ],
      },
    ],
    'information_retrieval',
  ),
  withoutMenuSection([
    {
      path: '/users/:userId',
      name: 'userProfile',
      meta: {
        title: 'メンバーページ',
        type: 'user',
        accessScope: 'information_retrieval',
      },
      redirect: route => {
        return `/users/${route.params.userId}/marks`;
      },
    },
    {
      path: '/users/:userId/marks',
      component: UserMarks,
      name: 'userMarks',
      meta: {
        title: 'メンバーページ',
        type: 'user',
        accessScope: 'information_retrieval',
      },
      props: true,
    },
    {
      path: '/web-push/redirect-to',
      component: redirectWebPushPage,
      name: 'redirect_web_push_page',
      meta: {
        type: 'no_login_required_redirect',
        accessScope: 'public',
      },
    },
  ]),
];

export const routes: RouteRecordRaw[] = [
  // メールから記事を開く際にユーザーのメーラーが、
  // 計測用エンドポイントにアクセスすることを防ぐためにクッションページを挟む
  {
    path: '/r/:docType/:articleId',
    component: redirectEmailPage,
    name: 'redirect_page',
    meta: {
      type: 'no_login_required_redirect',
      accessScope: 'public',
    },
  },
  {
    path: '/rs/personal_feed_summary/:docType',
    component: {
      template: '',
    },
    name: 'personal_feed_summary_redirect_page',
    meta: {
      type: 'no_login_required_redirect',
      accessScope: 'public',
    },
    beforeEnter: to => {
      const docType = to.params.docType;
      const queryString = to.fullPath.split('?')[1];
      const redirectUrl = `/api/v1/redirect/feed_summaries/personal_feed/${docType}?${queryString}`;
      location.href = redirectUrl;
    },
  },
  {
    path: '/rs/theme_summary/:themeId/:docType',
    component: {
      template: '',
    },
    name: 'theme_feed_summary_redirect_page',
    meta: {
      type: 'no_login_required_redirect',
      accessScope: 'public',
    },
    beforeEnter: to => {
      const themeId = to.params.themeId;
      const docType = to.params.docType;
      const queryString = to.fullPath.split('?')[1];
      const redirectUrl = `/api/v1/redirect/feed_summaries/theme/${themeId}/${docType}?${queryString}`;
      location.href = redirectUrl;
    },
  },

  accountRouteConfig,
  dashboardRouteConfig,
  onboardingRouteConfig,
  labsRouteConfig,
  patentFtoRouteConfig,
  techExplorationRouteConfigs,
  ...(featureFlags.ANDEV_9078_FORCED_IDEATION
    ? [forcedIdeationRouteConfig]
    : []),
  ...(featureFlags.ANDEV_9479_MARKET_SIZING ? [marketSizingRouteConfig] : []),
  ...(featureFlags.ANDEV_10285_FMEA ? [fmeaRouteConfig] : []),
  {
    path: '/',
    component: MainViewSwitcher,
    children: shellRoutes,
  },
  {
    path: '/redirect',
    meta: { accessScope: 'public' },
    beforeEnter: async (to, _, next) => {
      const sendViewParamsRaw = localStorage.getItem('redirectSendViewParams');
      if (sendViewParamsRaw !== null) {
        localStorage.removeItem('redirectSendViewParams');
        try {
          const sendViewParams = JSON.parse(sendViewParamsRaw);
          await api.sendView(sendViewParams);
        } catch (error) {
          Sentry.captureMessage(error as string, 'warning');
        }
      }
      if (typeof to.query.url === 'string') {
        location.href = `${to.query.url}${to.hash}`;

        // リダイレクト先が同じAnewsドメインの場合はリロードする(リロードしないとVueアプリケーションが読み込まれないため)
        if (to.query.url.includes(location.host)) {
          location.reload();
        }
      } else {
        // beforeEnterで記事に飛べないときはホームに飛ばす
        next({ name: 'anewsHome' });
      }
    },
    // 型定義のためにdummyで設定
    component: {
      template: '<div></div>',
    },
  },
  {
    path: '/mobile_app_download',
    meta: {
      type: 'mobile_app_download',
      accessScope: 'public',
    },
    beforeEnter() {
      location.href = '/api/v1/mobile_app/store_redirect';
    },
    // 型定義のためにdummyで設定
    component: {
      template: '<div></div>',
    },
  },
  { path: '/:pathMatch(.*)', redirect: '/', meta: { accessScope: 'public' } },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes: isMobileUser() ? mobileRoutes : routes,
  scrollBehavior(to, from, savedPosition) {
    // 要約機能で初回の処理時のみ必要だったクエリパラメータを`router.replace`で消す処理を行った際に、スクロール位置がtopに戻るのを防ぐ
    if (to.meta && to.meta.preserveScroll) {
      return savedPosition ? savedPosition : false;
    }

    if (to.path === from.path && savedPosition) {
      return savedPosition;
    }

    // 同一ページ内でアンカー（hash / アンカークエリ）での該当要素までのスクロールが、
    // Vue RouterのscrollBehaviorの挙動によって妨げられるのを防ぐ
    if (
      to.path === from.path &&
      (to.hash || to.query[TECH_EXPLORATION_ANCHOR_QUERY_KEY])
    ) {
      return false;
    }

    const layoutScrollElement = getLayoutScrollElement();
    if (layoutScrollElement) {
      layoutScrollElement.scrollTo({ left: 0, top: 0 });
      return false;
    }

    return { left: 0, top: 0 };
  },
});

// 権限情報の取得を待つ間に次の遷移が始まると、先の遷移の判定が後から完了してしまうため、
// 最新の遷移の判定結果だけを反映する
let latestNavigationId = 0;

// 戻る・進む・アドレスバーでの遷移で、履歴が何件動いたか。ガードで遷移を取り消したときに、履歴を戻すために使う。
// vue-routerより先に登録されるので、その遷移のガードより前に記録される。
let pendingHistoryDelta: number | null = null;
router.options.history.listen((_to, _from, info) => {
  pendingHistoryDelta = info.delta;
});

type FunctionAccessDecision =
  | { kind: 'proceed' }
  | { kind: 'redirect'; location: RouteLocationRaw }
  | { kind: 'loginRequired' };

// nextを呼ばずに判定結果だけを返し、古い遷移かどうかの確認を呼び出し側の1か所にまとめる
const resolveFunctionAccessDecision = async (
  to: RouteLocationNormalized,
): Promise<FunctionAccessDecision> => {
  const hasRoute = (routeName: string) => router.hasRoute(routeName);
  try {
    if ((await resolveAccessDenialReason(to)) !== null) {
      return {
        kind: 'redirect',
        location: await resolveFallbackLocation(hasRoute),
      };
    }
    // 情報収集を利用できないユーザーのホームは選択画面になるため、
    // 利用したことのある機能があればそのトップへ送る。
    if (to.name === 'anewsHome') {
      const location = await resolveLastUsedAgentLocation(hasRoute);
      if (location !== null) return { kind: 'redirect', location };
    }
    return { kind: 'proceed' };
  } catch (error) {
    if (error instanceof LoginRequiredError) return { kind: 'loginRequired' };
    throw error;
  }
};

router.beforeEach(async (to, from, next) => {
  const navigationId = ++latestNavigationId;
  const historyDelta = pendingHistoryDelta;
  pendingHistoryDelta = null;
  store.commit('route/setPreviousRoute', from);
  if (shouldRedirectToLogin(to)) {
    next({ name: 'login', query: { redirect: to.fullPath } });
    return;
  }
  if (to.name === 'login' && userSession.isActive()) {
    next({ name: 'anewsHome' });
    return;
  }

  const designRedirect = redirectToCurrentDesign(to);
  if (designRedirect) {
    next(designRedirect);
    return;
  }

  if (shouldRedirectToMypage(to)) {
    redirectToMypage(to, next);
    return;
  }

  const decision = await resolveFunctionAccessDecision(to);
  // 判定中に次の遷移が始まっていたら、新しい遷移の側であらためて判定するので、この結果は使わない
  if (navigationId !== latestNavigationId) {
    next();
    return;
  }

  switch (decision.kind) {
    case 'redirect':
      // 遷移先が今いる画面だと、vue-routerは重複した遷移として扱い、アドレスバーで変えたURLを戻さない。
      // 遷移を取り消せば、vue-routerが開く前の履歴に戻してURLも戻る。初回の遷移は今いる画面が無いので対象外。
      if (
        from.matched.length > 0 &&
        router.resolve(decision.location).fullPath === from.fullPath
      ) {
        next(false);
        // redirectを持つルートへの履歴の移動だけは、vue-routerが履歴を戻さないので、動いた分をここで戻す。
        // アドレスバーでURLを変えたときは動いた件数が0になるので、1件戻す（vue-routerと同じ扱い）。
        if (to.redirectedFrom && historyDelta !== null) {
          router.options.history.go(historyDelta ? -historyDelta : -1, false);
        }
        return;
      }
      next(decision.location);
      return;
    case 'loginRequired':
      clearLocalSession(useGTag());
      next({ name: 'login', query: { redirect: to.fullPath } });
      return;
    case 'proceed':
      next();
  }
});

const DEFAULT_TITLE = PRODUCT_NAME;

router.afterEach((to, _from, failure) => {
  if (failure) return;
  recordAgentVisit(to.meta.accessScope);

  const title = to.meta.title
    ? `${to.meta.title} | ${DEFAULT_TITLE}`
    : DEFAULT_TITLE;
  document.title = title;
});

const RELOAD_GUARD_KEY = 'dynamicImportReloadAt';
// 同一デプロイ境界での無限リロードを避けるためのクールダウン
const RELOAD_GUARD_INTERVAL_MS = 10000;

// 動的インポート失敗（ANDEV-9612 / STOCKMARK-5ZC）時は、最新の成果物を取得するため
// 遷移先へフルリロードして復帰させる。ただし無限リロードは防ぐ。
router.onError((error, to) => {
  if (!isDynamicImportError(error)) return;

  const now = Date.now();
  const lastReloadedAt = Number(
    sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0',
  );
  // 直近でリロード済みなら、リロードループを避けて Sentry に記録するだけに留める
  if (lastReloadedAt && now - lastReloadedAt < RELOAD_GUARD_INTERVAL_MS) {
    Sentry.captureException(error);
    return;
  }

  sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  // hash history のため、遷移先の hash を設定した上でフルリロードして chunk を再取得する
  window.location.hash = to.fullPath;
  window.location.reload();
});

// ナビゲーションガードや非同期コンポーネントの解決中に発生したエラーはmessageが
// 空のまま送出されることがあり、Sentry上で "No error message" となって発生箇所の
// 特定ができなくなる（例: STOCKMARK-7AV）。ここでmessageが空の場合のみ遷移元・
// 遷移先の情報を付与し、原因を可視化できるようにする。
// aborted/cancelled/duplicatedはこのハンドラに到達する前にvue-router内部で
// 処理されるため、ここに来る時点でerrorはNavigationFailureではない想定。
router.onError((error, to, from) => {
  if (error instanceof Error && !error.message) {
    try {
      error.message = `Router navigation error from "${from.fullPath}" to "${to.fullPath}"`;
    } catch {
      // DOMExceptionなどmessageがreadonlyのエラーは書き換えを諦める
    }
  }
});

export default router;
